import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Хук для подписки на socket.io события и автоматической отписки при размонтировании компонента.
 *
 * @param {Function[]} listeners - массив thunk-функций, каждая из которых регистрирует socket-событие
 * и возвращает функцию для отписки от него.
 *
 * Используется для централизованного управления подписками и отписками на сокет-события в компонентах.
 *
 * Пример использования:
 * ```js
 * useSocketListener([
 *   listenBrigadeCompositionInfo,
 *   listenBrigadeAllSignalsInfo,
 * ]);
 * ```
 *
 * Внутри `listenBrigadeCompositionInfo` и других функций должен возвращаться отписчик:
 * ```js
 * export const listenBrigadeCompositionInfo = () => (dispatch, getState, { socket }) => {
 *   socket.on('brigade_get_reply', (payload) => {
 *     dispatch(initBrigadeCompositionInfo(payload.data));
 *   });
 *
 *   return () => {
 *     socket.off('brigade_get_reply');
 *   };
 * };
 * ```
 */
export const useSocketListener = (listeners = []) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Фильтруем только валидные thunk-функции
    const validListeners = listeners.filter((listener) => typeof listener === 'function');

    // Вызываем каждую и сохраняем функции отписки
    const unsubscribes = validListeners.map((listener) => {
      try {
        return dispatch(listener());
      } catch (e) {
        console.warn('useSocketListener: ошибка при подписке на событие', listener, e);
        return null;
      }
    });

    // При размонтировании вызываем отписчики
    return () => {
      unsubscribes.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // только при первом рендере
};
